/**
 * @description HeatmapService module.
 * @file
 * @copyright © Copyright IBI Group., created 2019. All use, disclosure, and/or reproduction of this material is prohibited unless authorized in writing. All rights reserved.
 * @module services/HeatmapService
 */

import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import * as HeatmapHelpers from '../helpers/HeatmapHelpers';
import CONSTANTS from '../config/Constants';

/**
 * @description Service to handle heatmap data.
 * @class
 */
class HeatmapService {

    /**
     * Creates an instance of HeatmapService.
     * @param {Object} dataserviceHandlers - an object containing hanlders for data service events.
     * @param {Object} notificationService - the global instance of teh notifications service.
     */
    constructor(dataserviceHandlers, notificationService) {
        this.dataServiceHandlers = {
            onDataServiceMessage: () => null,
            onDataServiceOpen: () => null,
            ...dataserviceHandlers
        };

        this.notificationService = notificationService;
        this.currentRoute = null;
        this.currentDate = null;
        this.liveUpdateTimeouts = {};
    }

    /**
     * @description Handles the receiving of a message from the dataService.
     * @param {Object} event - The event
     */
    handleDataServiceMessage(message) {
        const dateTodayString = HeatmapHelpers.getDateStringFromDate(
                new Date(message.composed)
            ),
            response = {
                date: dateTodayString,
                carriageways: message.carriageways.map(carriageway => {
                    return {
                        id: `${message.roadName}_${carriageway.directionId}`,
                        periods: carriageway.timeBins.reverse().map(timeBin => {
                            return {
                                ...timeBin,
                                timeframe: HeatmapHelpers.convertTimeBinToTimeframe(
                                    timeBin.timeBin,
                                    timeBin.timeBinMinutes
                                )
                            };
                        })
                    };
                })
            };

        this.dataServiceHandlers.onDataServiceMessage(response);
    }

    /**
     * @description Handles the opening of the dataService.
     */
    handleDataServiceOpen() {
        const notificationDetails = {
            ...CONSTANTS.NOTIFICATIONS.RECONNECTION_MESSAGE
        };

        this.isDisconnected &&
            this.notificationService.addNotification(notificationDetails);
        this.dataServiceHandlers.onDataServiceOpen &&
            this.dataServiceHandlers.onDataServiceOpen();
        this.isDisconnected = false;
    }

    /**
     * @description Handles the closing of the dataService.
     */
    handleDataServiceClose() {
        const action = this.createDataServiceConnection.bind(this),
            notificationDetails = {
                ...CONSTANTS.NOTIFICATIONS.DISCONNECTION_MESSAGE,
                action: action,
                actionButtonText: 'Reconnect'
            },
            notification = this.notificationService.addNotification(
                notificationDetails
            );

        this.isDisconnected = true;

        // Attempts to reopen connection after 20s if it has not been reopened in this time.
        this.reconnectTimeout = setTimeout(() => {
            if (this.heatmapWebSocketService.readyState === 3) {
                this.createDataServiceConnection();
                this.notificationService.removeNotification(notification.id);
            } else if (this.heatmapWebSocketService.readyState === 1) {
                this.notificationService.removeNotification(notification.id);
            }
        }, CONSTANTS.DATA_SERVICE_RECONNECT_DELAY);
    }

    /**
     * @description Creates a connection to the dataService.
     */
    createDataServiceConnection() {
        return new Promise((resolve, reject) => {
            clearTimeout(this.reconnectTimeout);

            this.heatmapWebSocketService = new HubConnectionBuilder()
                .withUrl(process.env.HEATMAP_WSS_API_URL)
                .withAutomaticReconnect()
                .configureLogging(LogLevel.Warning)
                .build();

            this.heatmapWebSocketService.on(
                'Initialise Journey Times',
                message => {
                    this.handleDataServiceMessage(message);
                }
            );
            this.heatmapWebSocketService.on('Update Journey Times', message => {
                this.handleDataServiceMessage(message);
            });
            this.heatmapWebSocketService.onclose(error =>
                this.handleDataServiceClose(error)
            );
            this.heatmapWebSocketService
                .start()
                .then(() => {
                    this.handleDataServiceOpen();
                    resolve();
                })
                .catch(error => {
                    const notification = {
                        ...CONSTANTS.NOTIFICATIONS.CONNECTION_ERROR_MESSAGE,
                        additionalInformation: error
                    };

                    reject(notification);
                });
        });
    }

    /**
     * @description Gets the delay band data.
     * @returns {array} The delay bands.
     */
    getDelayData() {
        return new Promise((resolve, reject) =>
            fetch(
                process.env.API_ROOT_URL + process.env.DELAY_DATA_API_ENDPOINT
            )
                .then(response => {
                    if (!response.ok) {
                        throw new Error(response.statusText);
                    }

                    return response.json();
                })
                .then(data => resolve(data))
                .catch(error => {
                    const notification = {
                        ...CONSTANTS.NOTIFICATIONS.DELAY_DATA_ERROR_MESSAGE,
                        additionalInformation: error
                    };

                    reject(notification);
                })
        );
    }

    /**
     * @description Gets the available routes.
     * @returns {array} The list of routes.
     */
    getRoutes() {
        return new Promise((resolve, reject) =>
            fetch(process.env.API_ROOT_URL + process.env.ROUTES_API_ENDPOINT)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(response.statusText);
                    }

                    return response.json();
                })
                .then(data => {
                    data.forEach(route => {
                        let junctions = [];

                        route.carriageways.forEach(carriageway => {
                            carriageway.links.sort(
                                (a, b) => a.displayOrder - b.displayOrder
                            );
                            carriageway.junctions.sort(
                                (a, b) => a.displayOrder - b.displayOrder
                            );

                            const startLinkId = carriageway.links[0].id,
                                endLinkId =
                                    carriageway.links[
                                        carriageway.links.length - 1
                                    ].id,
                                startLinkJunction = carriageway.junctions.find(
                                    junction => junction.linkId === startLinkId
                                ),
                                endLinkJunction = carriageway.junctions.find(
                                    junction => junction.linkId === endLinkId
                                );

                            if (!startLinkJunction) {
                                carriageway.junctions.push({
                                    directionId: carriageway.direction.id,
                                    displayOrder: 0,
                                    label: 'Start',
                                    linkId: startLinkId,
                                    name: 'Start'
                                });
                            }

                            if (!endLinkJunction) {
                                carriageway.junctions.push({
                                    directionId: carriageway.direction.id,
                                    displayOrder:
                                        carriageway.junctions[
                                            carriageway.junctions.length - 1
                                        ].displayOrder + 1,
                                    label: 'End',
                                    linkId: endLinkId,
                                    name: 'End'
                                });
                            }

                            carriageway.junctions = carriageway.junctions.map(
                                junction => {
                                    const isJunction = !!parseInt(
                                        junction.label
                                    );

                                    return {
                                        ...junction,
                                        label: junction.label,
                                        isJunction: isJunction
                                    };
                                }
                            );
                            carriageway.id = `${route.name}_${carriageway.direction.id}`;
                            let carriagewayJunctions = carriageway.junctions
                                .filter(
                                    junction =>
                                        !junctions.find(
                                            existingJunction =>
                                                existingJunction ===
                                                junction.label ||
                                                junction.label === ''
                                        )
                                )
                                .map(junction => junction.label);

                            junctions = [...junctions, ...carriagewayJunctions];
                        });
                        route.junctions = junctions.sort((a, b) => {
                            const parsedA = parseInt(a) || a,
                                parsedB = parseInt(b) || b;

                            // Sort junctions in following order (where applicable):
                            //      'Start',
                            //      <numbers ascending...>,
                            //      <strings ascending>,
                            //      'End'
                            if (parsedA === 'Start' || parsedB === 'End') {
                                return -1;
                            } else if (
                                parsedA === 'End' ||
                                parsedB === 'Start'
                            ) {
                                return 1;
                            } else if (
                                typeof parsedA === 'string' &&
                                typeof parsedB === 'string' &&
                                parsedA.toUpperCase() < parsedB.toUpperCase()
                            ) {
                                return -1;
                            } else if (
                                typeof parsedA === 'string' &&
                                typeof parsedB === 'string' &&
                                parsedA.toUpperCase() > parsedB.toUpperCase()
                            ) {
                                return 1;
                            } else if (
                                typeof parsedA === 'string' &&
                                typeof parsedB === 'string'
                            ) {
                                return 0;
                            } else if (typeof parsedA === 'string') {
                                return 1;
                            } else if (typeof parsedB === 'string') {
                                return -1;
                            }

                            return parseInt(parsedA) - parseInt(parsedB);
                        });

                        resolve(data);
                    })
                }).catch(error => {
                    const notification = {
                        ...CONSTANTS.NOTIFICATIONS.ROUTE_DATA_ERROR_MESSAGE,
                        additionalInformation: error
                    };

                    reject(notification);
                })
        );
    }

    /**
     * @description Gets the data for a given route and date.
     * @param {string} route - The route name.
     * @param {Object} date - The date for which data should be obtained.
     * @returns {Object} - The returned data.
     */
    getHeatmapData(route, date) {
        const dateTodayString = HeatmapHelpers.getDateStringFromDate(
            new Date()
        );

        date = date === '' ? dateTodayString : date;

        this.currentDoute = route;
        this.currentDate = date;

        const queryDate = date ? date : dateTodayString,
            url = `${process.env.API_ROOT_URL +
                process.env
                    .JOURNEY_TIME_API_ENDPOINT}/${route}?date=${queryDate}&download=false`;

        return new Promise((resolve, reject) =>
            fetch(url)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(response.statusText);
                    }

                    return response.json();
                })
                .then(json => {
                    const data = {
                        date: queryDate,
                        carriageways: json.map(carriageway => {
                            return {
                                id: `${route}_${carriageway.directionId}`,
                                periods: carriageway.timeBins
                                    .reverse()
                                    .map(timeBin => {
                                        return {
                                            ...timeBin,
                                            timeframe: HeatmapHelpers.convertTimeBinToTimeframe(
                                                timeBin.timeBin,
                                                timeBin.timeBinMinutes
                                            )
                                        };
                                    })
                            };
                        })
                    };

                    resolve(data);
                })
                .catch(error => {
                    const notification = {
                        ...CONSTANTS.NOTIFICATIONS.HEATMAP_DATA_ERROR_MESSAGE,
                        additionalInformation: error
                    };

                    reject(notification);
                })
        );
    }
}

export default HeatmapService;
